import dayjs from "dayjs";
import { useShallow } from "zustand/react/shallow";

import { daysOfTheWeek } from "@/constants/calendar";
import useViewMode from "@/hooks/useViewMode";
import useCalendarStore from "@/store/calendarStore";
import type { CalendarItemProps } from "@/types/calendar";
import type { DailyReservation, DailyReservationData } from "@/types/reservation";
import { getWeeks } from "@/utils/calendar";
import { cn } from "@/utils/cn";

const MAX_PILLS = 2;

const formatTime = (time: string) => {
  if (!time || time.length < 4) return "";
  return `${time.slice(0, 2)}:${time.slice(2, 4)}`;
};

const pillColor: Record<string, string> = {
  approve: "bg-green-100 text-green-700",
  request: "bg-amber-100 text-amber-700",
  reject: "bg-red-100 text-red-600",
  cancel: "bg-gray-100 text-gray-400",
  deleted: "bg-gray-100 text-gray-300 line-through",
};

const legendItemsAdmin = [
  { label: "승인", cls: "bg-green-500" },
  { label: "대기", cls: "bg-amber-400" },
  { label: "반려", cls: "bg-red-400" },
  { label: "취소", cls: "bg-gray-400" },
];

const legendItemsUser = [
  { label: "승인", cls: "bg-green-500" },
  { label: "대기", cls: "bg-amber-400" },
];

const CalendarHead = () => (
  <thead>
    <tr className="flex items-center justify-around border-b border-gray-light bg-gray-50">
      {daysOfTheWeek.map((dayOfTheWeek, i) => (
        <CalendarItem
          key={dayOfTheWeek}
          type="header"
          dayOfTheWeek={i}
          className="cursor-default font-medium"
        >
          {dayOfTheWeek}
        </CalendarItem>
      ))}
    </tr>
  </thead>
);

interface CalendarItemWithReseravtion extends CalendarItemProps {
  dailyReservation?: DailyReservation[];
}

const CalendarItem = ({
  children,
  dailyReservation = [],
  type,
  dayOfTheWeek,
  dayData,
  isInactive = false,
  isSelected = false,
  className,
  ...props
}: CalendarItemWithReseravtion) => {
  const isHeader = type === "header";
  const isBody = type === "body";
  const isToday = dayData?.isSame(dayjs(), "day");

  // Flatten all reservation data for this day
  const allData: DailyReservationData[] = dailyReservation.flatMap(r => r.data);
  const visiblePills = allData.slice(0, MAX_PILLS);
  const remainingCount = allData.length - MAX_PILLS;

  return (
    <td
      className={cn(
        "relative flex w-[14.285%] min-h-[5rem] cursor-pointer flex-col items-center justify-start gap-0.5 border-r border-gray-light px-0.5 text-base font-light text-black transition-all duration-150 last-of-type:border-none",
        isHeader && "min-h-0 h-10 items-center",
        isBody && "pt-1 hover:-translate-y-px hover:shadow-md hover:z-10 hover:rounded-lg hover:bg-blue-50/40",
        isInactive && "text-opacity-30",
        dayOfTheWeek === 6 && "text-saturday",
        dayOfTheWeek === 0 && "text-red",
        className,
      )}
      {...props}
    >
      {
        <div
          className={`z-40 flex h-[26px] w-[26px] items-center justify-center text-sm ${isToday && "rounded-full bg-primary text-white"}`}
        >
          {children}
        </div>
      }

      {isBody && allData.length > 0 && (
        <div className="z-40 flex w-full flex-col gap-0.5 px-0.5">
          {visiblePills.map((item, idx) => (
            <div
              key={idx}
              className={cn(
                "truncate rounded px-1 py-px text-[9px] leading-tight",
                pillColor[item.sttCd] || pillColor.approve,
              )}
            >
              {formatTime(item.startTime)}-{formatTime(item.endTime)} {item.useCnts}
            </div>
          ))}
          {remainingCount > 0 && (
            <div className="px-1 text-[9px] leading-tight text-gray-400">
              +{remainingCount}건
            </div>
          )}
        </div>
      )}

      <div
        className={`absolute left-0 top-0 h-full w-full ${isSelected && "rounded-lg border border-primary bg-primary-light bg-opacity-30"}`}
      ></div>
    </td>
  );
};

interface CalendarComponentProps {
  onDateClick?: (day: dayjs.Dayjs) => void;
  isDaySelected?: boolean;
}

const Calendar = ({ onDateClick, isDaySelected = true }: CalendarComponentProps) => {
  const { isAdmin } = useViewMode();
  const legendItems = isAdmin ? legendItemsAdmin : legendItemsUser;
  const [
    date,
    setDate,
    firstDayOfMonth,
    setFirstDayOfMonth,
    monthlyReservations,
  ] = useCalendarStore(
    useShallow(state => [
      state.date,
      state.setDate,
      state.firstDayOfMonth,
      state.setFirstDayOfMonth,
      state.monthlyReservations,
    ]),
  );

  return (
    <div>
      <table className="mx-auto flex w-full border-collapse flex-col">
        <CalendarHead />
        <tbody className="flex flex-1 flex-col">
          {getWeeks(firstDayOfMonth).map(week => (
            <tr
              key={week[0].valueOf()}
              className="flex border-b border-gray-light last-of-type:border-none"
            >
              {week.map(day => {
                const isCurrentMonth = dayjs(date).month() === day.month();
                const isInactive = !isCurrentMonth;

                const dailyReservation = monthlyReservations
                  ? monthlyReservations.filter(
                      (reservation: DailyReservation) =>
                        day.date() === dayjs(reservation.day).date() &&
                        !isInactive,
                    )
                  : [];

                return (
                  <CalendarItem
                    key={day.valueOf()}
                    type="body"
                    dailyReservation={dailyReservation}
                    dayOfTheWeek={day.day()}
                    dayData={day}
                    isInactive={isInactive}
                    isSelected={isDaySelected && dayjs(date)?.isSame(day, "day")}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDate(day);
                      if (isInactive) setFirstDayOfMonth(day.date(1));
                      onDateClick?.(day);
                    }}
                  >
                    {day.date()}
                  </CalendarItem>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Legend */}
      <div className="mt-3 flex items-center justify-end gap-4 px-2">
        {legendItems.map(({ label, cls }) => (
          <div key={label} className="flex items-center gap-1.5">
            <span className={cn("h-2.5 w-2.5 rounded-full", cls)} />
            <span className="text-[11px] text-gray-500">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Calendar;
